import { createModalStoreFactory } from '@/features/_shared/stores/create-modal.store';
import { useShallow } from 'zustand/react/shallow';
import type {
  ICreateSpaceModalProps,
  TCreateSpaceModalType,
} from '../../../spaces/space/components/modals/create-space-modal/create-space-modal';
import type {
  IDeleteSpaceModalProps,
  TDeleteSpaceModalType,
} from '../../../spaces/space/components/modals/delete-space-modal/delete-space-modal';
import type {
  IShareSpaceModalProps,
  TShareSpaceModalType,
} from '../../../spaces/space/components/modals/share-space-modal/share-space-modal';
import type {
  IUpdateSpaceModalProps,
  TUpdateSpaceModalType,
} from '../../../spaces/space/components/modals/update-space-modal/update-space-modal';
import type {
  ICreateLabelTicketModalProps,
  TCreateLabelTicketModalType,
} from '../../../label-ticketing/components/modals/create-label-ticket-modal';
import type {
  IUpdateLabelTicketModalProps,
  TUpdateLabelTicketModalType,
} from '../../../label-ticketing/components/modals/update-label-ticket-modal';
import type {
  IDeleteLabelTicketModalProps,
  TDeleteLabelTicketModalType,
} from '../../../label-ticketing/components/modals/delete-label-ticket-modal/delete-label-ticket-modal';
import type {
  TCreateColumnModalProps,
  TCreateColumnModalType,
} from '../../../columns/column/components/create-column-modal/create-column-modal';
import type {
  TUpdateColumnModalProps,
  TUpdateColumnModalType,
} from '../../../columns/column/components/update-column-modal/update-column-modal';
import type {
  IDeleteColumnModalProps,
  TDeleteColumnModalType,
} from '../../../columns/column/components/delete-column-modal/delete-column-modal';
import type {
  IDeleteTicketModalProps,
  TDeleteTicketModalType,
} from '../../../tickets/ticket/components/modals/delete-ticket-modal/delete-ticket-modal';
import type {
  ICreateTicketModalProps,
  TCreateTicketModalType,
} from '../../../tickets/ticket/components/modals/create-ticket-modal';
import type {
  ICreateBulkTicketModalProps,
  TCreateBulkTicketModalType,
} from '../../../tickets/ticket/components/modals/create-bulk-ticket-modal/create-bulk-ticket-modal';
import type {
  IUpdateTicketModalProps,
  TUpdateTicketModalType,
} from '../../../tickets/ticket/components/modals/update-ticket-modal/update-ticket-modal';
import type {
  IFilterTicketModalProps,
  TFilterTicketModalType,
} from '../../../tickets/ticket/components/modals/filter-ticket-modal/filter-ticket-modal';
import type {
  IDuplicateTicketModalProps,
  TDuplicateTicketModalType,
} from '../../../tickets/ticket/components/modals/duplicate-ticket-modal/duplicate-ticket-modal';
import type {
  ICompleteTicketModalProps,
  TCompleteTicketModalType,
} from '../../../tickets/ticket/components/modals/complete-ticket-modal/complete-ticket-modal';
import type {
  IArchiveTicketModalProps,
  TArchiveTicketModalType,
} from '../../../tickets/ticket/components/modals/archive-ticket-modal/archive-ticket-modal';
import type {
  IRestoreTicketModalProps,
  TRestoreTicketModalType,
} from '../../../tickets/ticket/components/modals/restore-ticket-modal/restore-ticket-modal';
import type {
  IArchiveAllTicketModalProps,
  TArchiveAllTicketModalType,
} from '../../../tickets/ticket/components/modals/archive-all-ticket-modal/archive-all-ticket-modal';
import type {
  IRestoreAllTicketModalProps,
  TRestoreAllTicketModalType,
} from '../../../tickets/ticket/components/modals/restore-all-ticket-modal';
import type {
  IDeleteSpaceMemberModalProps,
  TDeleteSpaceMemberModalType,
} from '../../../spaces/space-member/components/modals/delete-space-member/delete-space-member';
import type {
  IDeleteUserTicketingModalProps,
  TDeleteUserTicketingModalType,
} from '../../../ticketing-user/components/delete-ticketing-user-modal/delete-ticketing-user-modal';
import type {
  ISetAsAdminTicketingUserModalProps,
  TSetAsAdminTicketingUserModalType,
} from '../../../ticketing-user/components/set-as-admin-ticketing-user-modal/set-as-admin-ticketing-user-modal';
import type {
  IDeleteInvitationTicketingUserModalProps,
  TDeleteInvitationTicketingUserModalType,
} from '../../../ticketing-user/components/delete-invitation-ticketing-user-modal/delete-invitation-ticketing-user-modal';
import type {
  IInvitationTicketingUserModalProps,
  TInvitationTicketingUserModalType,
} from '../../../ticketing-user/components/list-invitation-ticketing-user-modal/list-invitation-ticketing-user-modal';
import type {
  IResendInvitationTicketingUserModalProps,
  TResendInvitationTicketingUserModalType,
} from '../../../ticketing-user/components/resend-invitation-ticketing-user-modal/resend-invitation-ticketing-user-modal';
import type {
  IInviteAsAdminTicketingUserModalProps,
  TInviteAsAdminTicketingUserModalType,
} from '../../../ticketing-user/components/invite-as-admin-ticketing-user-modal/invite-as-admin-ticketing-user-modal';

export type TTicketingModalRootTypes =
  // Space Modal
  | TCreateSpaceModalType
  | TDeleteSpaceModalType
  | TShareSpaceModalType
  | TUpdateSpaceModalType
  | TDeleteSpaceMemberModalType

  // Label Ticket Modal
  | TCreateLabelTicketModalType
  | TUpdateLabelTicketModalType
  | TDeleteLabelTicketModalType

  // Column Modal
  | TCreateColumnModalType
  | TUpdateColumnModalType
  | TDeleteColumnModalType

  // Ticket Modal
  | TCreateTicketModalType
  | TCreateBulkTicketModalType
  | TUpdateTicketModalType
  | TDeleteTicketModalType
  | TFilterTicketModalType
  | TDuplicateTicketModalType
  | TCompleteTicketModalType
  | TArchiveTicketModalType
  | TArchiveAllTicketModalType
  | TRestoreTicketModalType
  | TRestoreAllTicketModalType

  // Ticketing User Modal
  | TDeleteUserTicketingModalType
  | TSetAsAdminTicketingUserModalType
  | TDeleteInvitationTicketingUserModalType
  | TInvitationTicketingUserModalType
  | TResendInvitationTicketingUserModalType
  | TInviteAsAdminTicketingUserModalType;

interface TTicketingModalRootProps {
  // Space Modal
  CreateSpaceModal: ICreateSpaceModalProps;
  DeleteSpaceModal: IDeleteSpaceModalProps;
  ShareSpaceModal: IShareSpaceModalProps;
  UpdateSpaceModal: IUpdateSpaceModalProps;

  DeleteSpaceMemberModal: IDeleteSpaceMemberModalProps;

  // Label Ticket Modal
  CreateLabelTicketModal: ICreateLabelTicketModalProps;
  UpdateLabelTicketModal: IUpdateLabelTicketModalProps;
  DeleteLabelTicketModal: IDeleteLabelTicketModalProps;

  // Column Modal
  CreateColumnModal: TCreateColumnModalProps;
  UpdateColumnModal: TUpdateColumnModalProps;
  DeleteColumnModal: IDeleteColumnModalProps;

  // Ticket Modal
  DeleteTicketModal: IDeleteTicketModalProps;
  UpdateTicketModal: IUpdateTicketModalProps;
  CreateTicketModal: ICreateTicketModalProps;
  CreateBulkTicketModal: ICreateBulkTicketModalProps;
  FilterTicketModal: IFilterTicketModalProps;
  DuplicateTicketModal: IDuplicateTicketModalProps;
  CompleteTicketModal: ICompleteTicketModalProps;
  ArchiveTicketModal: IArchiveTicketModalProps;
  ArchiveAllTicketModal: IArchiveAllTicketModalProps;
  RestoreTicketModal: IRestoreTicketModalProps;
  RestoreAllTicketModal: IRestoreAllTicketModalProps;

  // Ticketing User Modal
  DeleteUserTicketingModal: IDeleteUserTicketingModalProps;
  SetAsAdminTicketingUserModal: ISetAsAdminTicketingUserModalProps;
  DeleteInvitationTicketingUserModal: IDeleteInvitationTicketingUserModalProps;
  InvitationTicketingUserModal: IInvitationTicketingUserModalProps;
  ResendInvitationTicketingUserModal: IResendInvitationTicketingUserModalProps;
  InviteAsAdminTicketingUserModal: IInviteAsAdminTicketingUserModalProps;
}

export const useTicketingModalRootStore = createModalStoreFactory<TTicketingModalRootTypes, TTicketingModalRootProps>();

export const useTicketingModalRootActions = () => useTicketingModalRootStore(useShallow((state) => state.actions));
